/*
 * Storefront theme scripts: small vanilla custom elements, no dependencies.
 * Everything is progressive enhancement — forms and links work without JS.
 */
(() => {
  'use strict';

  const theme = (window.theme = window.theme || {});
  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const CART_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  /* ---------- Helpers ---------- */

  function announce(message) {
    const region = document.getElementById('a11y-live');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 60);
  }

  // Cart sections to re-render: the drawer + header count, plus the cart page section when present.
  function cartSectionIds() {
    const ids = new Set(CART_SECTIONS);
    document.querySelectorAll('[data-cart-section]').forEach((el) => ids.add(el.dataset.cartSection));
    return [...ids].slice(0, 5);
  }

  // Swap the inner HTML of every [data-section-target="<id>"] with the fresh render.
  function renderSections(sections) {
    if (!sections) return;
    const focusedId = document.activeElement && document.activeElement.id;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(`[data-section-target="${id}"]`);
      if (!fresh) return;
      document.querySelectorAll(`[data-section-target="${id}"]`).forEach((target) => {
        target.replaceChildren(...fresh.cloneNode(true).childNodes);
      });
    });
    if (focusedId) {
      const next = document.getElementById(focusedId);
      if (next && next !== document.activeElement) next.focus({ preventScroll: true });
    }
  }

  async function cartRequest(url, payload) {
    const sections = cartSectionIds();
    const headers = { Accept: 'application/json' };
    let body;
    if (payload instanceof FormData) {
      payload.append('sections', sections.join(','));
      payload.append('sections_url', window.location.pathname);
      body = payload;
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify({ ...payload, sections, sections_url: window.location.pathname });
    }
    const response = await fetch(`${url}.js`, { method: 'POST', headers, body });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || theme.strings.error);
    renderSections(data.sections);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    return data;
  }

  async function refreshCart() {
    const response = await fetch(`${window.location.pathname}?sections=${cartSectionIds().join(',')}`);
    if (response.ok) renderSections(await response.json());
  }

  function showCartError(message) {
    document.querySelectorAll('[data-cart-error]').forEach((el) => {
      el.textContent = message;
      el.hidden = false;
    });
  }

  Object.assign(theme, { announce, renderSections, cartRequest, refreshCart });

  /* ---------- Cart drawer ---------- */

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-cart-close]')) {
          this.close();
          return;
        }
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        const outside =
          event.clientX < rect.left ||
          event.clientX > rect.right ||
          event.clientY < rect.top ||
          event.clientY > rect.bottom;
        if (outside) this.close();
      });
      this.dialog.addEventListener('close', () => {
        root.classList.remove('cart-open');
        if (this.trigger && this.trigger.isConnected) this.trigger.focus({ preventScroll: true });
      });
    }

    open(trigger) {
      if (!this.dialog) return;
      this.trigger = trigger || document.activeElement;
      if (!this.dialog.open) {
        this.dialog.showModal();
        root.classList.add('cart-open');
      }
    }

    close() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-cart-open]');
    if (!link || theme.cartType !== 'drawer') return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !drawer.querySelector('dialog')) return;
    event.preventDefault();
    drawer.open(link);
  });

  /* ---------- Add both colors ---------- */

  // One request for both variants, then the same drawer as add to cart.
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-add-both]');
    if (!button || button.classList.contains('is-loading')) return;
    const error = button.closest('product-form')?.querySelector('[data-form-error]');
    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');
    if (error) error.hidden = true;
    try {
      const items = button.dataset.addBoth.split(',').map((id) => ({ id: Number(id), quantity: 1 }));
      await cartRequest(theme.routes.cartAdd, { items });
      announce(theme.strings.added);
      const drawer = document.querySelector('cart-drawer');
      if (theme.cartType === 'drawer' && drawer) drawer.open(button);
      else window.location.href = theme.routes.cart;
    } catch (failure) {
      if (error) {
        error.textContent = failure.message;
        error.hidden = false;
      }
      announce(failure.message);
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
    }
  });

  /* ---------- Cart lines (drawer and cart page) ---------- */

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
    }

    onChange(event) {
      const input = event.target.closest('input[data-line-key]');
      if (!input) return;
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.update(input.dataset.lineKey, Number(input.value), input), 350);
    }

    onClick(event) {
      const remove = event.target.closest('[data-cart-remove]');
      if (!remove) return;
      event.preventDefault();
      this.update(remove.dataset.cartRemove, 0, remove);
    }

    async update(key, quantity, source) {
      if (!Number.isFinite(quantity) || quantity < 0) return;
      const line = source.closest('[data-cart-line]');
      if (line) line.classList.add('is-updating');
      this.setAttribute('aria-busy', 'true');
      try {
        await cartRequest(theme.routes.cartChange, { id: key, quantity });
        announce(theme.strings.updated);
      } catch (error) {
        await refreshCart();
        showCartError(error.message);
        announce(error.message);
      } finally {
        this.removeAttribute('aria-busy');
        if (line && line.isConnected) line.classList.remove('is-updating');
      }
    }
  }

  /* ---------- Product form ---------- */

  const ADDED_MS = 5000; // how long "added · view cart" stays when the drawer doesn't open by itself
  const OPEN_DELAY_MS = 500; // lets the seam's zip show before the drawer covers it

  // Replays the seal seam's zip. Its closed resting state is the end of the zip,
  // so without html.reveal-ready (no JS reveals, reduced motion) it simply stays closed.
  function zipSeam(seam) {
    if (!seam || reducedMotion.matches || !root.classList.contains('reveal-ready')) return;
    seam.classList.remove('is-visible');
    void seam.offsetWidth; // restart from open
    seam.classList.add('is-visible');
  }

  /*
   * With the cart drawer, the submit button reads "add to cart" → "adding…" →
   * "added · view cart" and the drawer opens; closing it turns the button back into
   * "add to cart". With cart_open_on_add off, a press while it says "added" opens the
   * drawer instead. The CTA rail's button submits the same form, so it gets the same states.
   */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.error = this.querySelector('[data-form-error]');
      if (!this.form) return;
      this.onSubmit = this.onSubmit.bind(this);
      this.onVariant = this.onVariant.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
      document.addEventListener('variant:change', this.onVariant);
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      document.removeEventListener('variant:change', this.onVariant);
      window.clearTimeout(this.addedTimer);
      window.clearTimeout(this.openTimer);
    }

    setLabel(button, key) {
      const label = button.querySelector('[data-add-label], [data-rail-add-label]');
      const text = button.dataset[key];
      if (label && text) label.textContent = text;
    }

    // Leave the "added" state: the next press adds to the cart again.
    clearAdded(restoreLabel) {
      window.clearTimeout(this.addedTimer);
      [...this.form.elements].forEach((button) => {
        if (!button.classList.contains('is-added')) return;
        button.classList.remove('is-added');
        if (restoreLabel) this.setLabel(button, button.disabled ? 'labelSoldOut' : 'labelAdd');
      });
    }

    // The variant picker and the CTA rail relabel their buttons for the new variant.
    onVariant(event) {
      const detail = event.detail || {};
      if (detail.formId === this.form.id) this.clearAdded(false);
    }

    openDrawer(drawer, dialog, button) {
      dialog.addEventListener('close', () => this.clearAdded(true), { once: true });
      const delay = reducedMotion.matches ? 0 : OPEN_DELAY_MS;
      this.openTimer = window.setTimeout(() => drawer.open(button), delay);
    }

    async onSubmit(event) {
      const drawer = document.querySelector('cart-drawer');
      if (theme.cartType !== 'drawer' || !drawer) return; // native POST to /cart/add
      event.preventDefault();
      const button = event.submitter || this.form.querySelector('[type="submit"]');
      if (!button || button.classList.contains('is-loading')) return;

      if (button.classList.contains('is-added')) {
        window.clearTimeout(this.openTimer);
        this.clearAdded(true);
        drawer.open(button);
        return;
      }

      this.clearAdded(true);
      button.classList.add('is-loading');
      button.setAttribute('aria-disabled', 'true');
      this.setLabel(button, 'labelAdding');
      if (this.error) this.error.hidden = true;

      try {
        await cartRequest(theme.routes.cartAdd, new FormData(this.form));
        this.setLabel(button, 'labelAdded');
        button.classList.add('is-added');
        zipSeam((button.closest('cta-rail') || this).querySelector('[data-add-seam]'));
        announce(theme.strings.added);
        const dialog = drawer.querySelector('dialog');
        if (theme.cartOpenOnAdd && dialog) {
          this.openDrawer(drawer, dialog, button);
        } else {
          this.addedTimer = window.setTimeout(() => this.clearAdded(true), ADDED_MS);
        }
      } catch (error) {
        this.setLabel(button, button.disabled ? 'labelSoldOut' : 'labelAdd');
        if (this.error) {
          this.error.textContent = error.message;
          this.error.hidden = false;
        }
        announce(error.message);
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      }
    }
  }

  /* ---------- Quantity stepper ---------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);
    }

    onClick(event) {
      const button = event.target.closest('button[data-step]');
      if (!button || !this.input) return;
      event.preventDefault();
      const min = this.input.min === '' ? 0 : Number(this.input.min);
      const max = this.input.max === '' ? Infinity : Number(this.input.max);
      const current = Number(this.input.value) || 0;
      const next = Math.min(max, Math.max(min, current + Number(button.dataset.step)));
      if (next === current) return;
      this.input.value = next;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* ---------- Product media gallery ---------- */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.counter = this.querySelector('[data-gallery-counter]');
      this.index = 0;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
          });
        },
        { root: this.track, threshold: 0.6 }
      );
      this.slides.forEach((slide) => this.observer.observe(slide));

      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-gallery-thumb]');
        if (thumb) this.goTo(Number(thumb.dataset.galleryThumb));
        if (event.target.closest('[data-gallery-prev]')) this.goTo(this.index - 1);
        if (event.target.closest('[data-gallery-next]')) this.goTo(this.index + 1);
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    goTo(target) {
      const count = this.slides.length;
      if (!count) return;
      const index = (target + count) % count;
      this.track.scrollTo({
        left: this.slides[index].offsetLeft,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
      this.setActive(index);
    }

    setActive(index) {
      if (index < 0) return;
      this.index = index;
      this.thumbs.forEach((thumb, n) => thumb.setAttribute('aria-current', String(n === index)));
      if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;
      const thumb = this.thumbs[index];
      const list = thumb && thumb.closest('[data-gallery-thumbs]');
      if (list) {
        const item = thumb.parentElement;
        const left = item.offsetLeft - (list.clientWidth - item.clientWidth) / 2;
        list.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
    }
  }

  /* ---------- Mobile menu ---------- */

  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      if (!this.details) return;
      this.onOutside = (event) => {
        if (this.details.open && !this.contains(event.target)) this.details.open = false;
      };
      document.addEventListener('click', this.onOutside);
      this.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape' || !this.details.open) return;
        this.details.open = false;
        this.details.querySelector('summary').focus();
      });
      this.addEventListener('click', (event) => {
        if (event.target.closest('a')) this.details.open = false;
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onOutside);
    }
  }

  /* ---------- Reveal on scroll ---------- */

  // The head script sets html.reveal-ready before the first paint (setting on, motion
  // allowed, outside the theme editor), so first-view effects start hidden instead of
  // twitching. It takes the class off again if this file has not armed it within 4 s.
  function initReveal() {
    if (!root.classList.contains('reveal-ready')) return;
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      root.classList.remove('reveal-ready');
      return;
    }
    theme.revealArmed = true;
    const items = document.querySelectorAll('.reveal:not(.is-visible), .seam:not(.is-visible)');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    items.forEach((item) => observer.observe(item));
  }

  /* ---------- Theme editor ---------- */

  document.addEventListener('shopify:block:select', (event) => {
    const details = event.target.closest('details') || event.target.querySelector('details');
    if (details) details.open = true;
  });

  /* ---------- Register ---------- */

  define('cart-drawer', CartDrawer);
  define('cart-items', CartItems);
  define('product-form', ProductForm);
  define('quantity-input', QuantityInput);
  define('media-gallery', MediaGallery);
  define('menu-drawer', MenuDrawer);
  initReveal();
})();
